'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Mail,
  ChevronRight,
  Loader2,
  CheckCircle,
  Shield,
  RefreshCw,
  ArrowLeft,
  Lock,
  BookOpen,
  ShieldCheck,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ApiError } from '@/lib/api';
import {
  fetchPortalConfig,
  requestPortalAccessOtp,
  resendPortalAccessOtp,
  verifyPortalAccessOtp,
} from '@/lib/books-api';
import {
  libraryPathWithToken,
  loadPortalAccessSession,
  savePortalSessionFromApi,
  type PortalAccessSession,
} from '@/lib/portal-access';

interface EmailForm {
  email: string;
}

type Step = 'email' | 'otp';

export default function OTPVerificationClient() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [otpRequired, setOtpRequired] = useState(true);
  const [savedSession, setSavedSession] = useState<PortalAccessSession | null>(
    null
  );
  const [resendCountdown, setResendCountdown] = useState(0);
  const [otpError, setOtpError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const checkedRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailForm>();

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    const urlToken =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('token')
        : null;

    // Email access links still open the library directly
    if (urlToken) {
      router.replace(libraryPathWithToken(urlToken));
      return;
    }

    setSavedSession(loadPortalAccessSession());

    // Fetch portal configuration to check if OTP is enabled globally
    fetchPortalConfig()
      .then((res) => {
        if (res.data) {
          setOtpRequired(Boolean(res.data.otp_required));
        }
      })
      .catch(() => {})
      .finally(() => {
        setChecking(false);
      });
  }, [router]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const openSavedLibrary = () => {
    if (!savedSession?.portal_token) return;
    router.push(libraryPathWithToken(savedSession.portal_token));
  };

  const onEmailSubmit = async (data: EmailForm) => {
    setLoading(true);
    try {
      const result = await requestPortalAccessOtp(data.email.trim());
      const targetEmail = data.email.trim().toLowerCase();
      setEmail(targetEmail);

      // If OTP is disabled globally, access is granted directly without step 2 OTP
      if (result.data?.direct_access || result.data?.otp_required === false) {
        const session = savePortalSessionFromApi(result.data || {});
        toast.success(
          session.books.length === 1
            ? 'Access granted — 1 flipbook available'
            : `Access granted — ${session.books.length} flipbooks available`
        );
        router.replace(libraryPathWithToken(session.portal_token));
        return;
      }

      setStep('otp');
      setResendCountdown(60);
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      toast.success(result.message || `OTP sent to ${data.email}`);
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : 'Failed to access library'
      );
    } finally {
      setLoading(false);
    }
  };


  const handleOTPChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setOtpError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otp];
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOTP = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setOtpError('Enter all 6 digits of your OTP');
      return;
    }

    setLoading(true);
    setOtpError('');
    try {
      const result = await verifyPortalAccessOtp(email, code);
      const session = savePortalSessionFromApi(result.data || {});

      if (result.data?.emailed) {
        toast.success('Access granted — link also sent to your email');
      } else {
        toast.success(
          session.books.length === 1
            ? 'Access granted — 1 flipbook available'
            : `Access granted — ${session.books.length} flipbooks available`
        );
      }

      router.replace(libraryPathWithToken(session.portal_token));
    } catch (error) {
      const message =
        error instanceof ApiError ? error.message : 'Failed to verify OTP';
      setOtpError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;
    setLoading(true);
    try {
      const result = await resendPortalAccessOtp(email);
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      setResendCountdown(60);
      toast.success(result.message || `New OTP sent to ${email}`);
      inputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : 'Failed to resend OTP'
      );
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="h-14 bg-card border-b border-border flex items-center px-6">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <div>
              <span className="font-700 text-sm text-foreground block leading-tight">
                Birla Open Minds
              </span>
              <span className="text-xs text-muted-foreground">International School</span>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={18} className="animate-spin text-primary" />
            Loading…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 bg-card border-b border-border flex items-center px-6">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <div>
            <span className="font-700 text-sm text-foreground block leading-tight">
              Birla Open Minds
            </span>
            <span className="text-xs text-muted-foreground">International School</span>
          </div>
        </div>
        <div className="flex-1" />
        <Link
          href="/"
          className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
        >
          Admin Login
          <ChevronRight size={13} />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-4">
          {step === 'email' && (
            <>
              <div className="bg-card border border-border rounded-xl p-6 animate-fade-in-up">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Mail size={20} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-base font-700 text-foreground">
                      {otpRequired ? 'Verify Your Email' : 'Access Your Flipbooks'}
                    </h1>
                    <p className="text-xs text-muted-foreground">
                      {otpRequired
                        ? 'Step 1 of 2 — Enter your email to receive an OTP'
                        : 'Enter your email address to access your flipbook library'}
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1.5">
                      Email Address
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Enter the email address your school shared flipbook access with
                    </p>
                    <div className="relative">
                      <Mail
                        size={15}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="email"
                        placeholder="your.name@school.edu"
                        className={`w-full pl-9 pr-4 py-2.5 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                          errors.email ? 'border-red-400' : 'border-border'
                        }`}
                        {...register('email', {
                          required: 'Email address is required',
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Enter a valid email address',
                          },
                        })}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  {otpRequired ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                      <Shield size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        A 6-digit one-time password will be sent to your email. After
                        verification you can open your flipbook library.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                      <ShieldCheck size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-emerald-700">
                        Enter your registered email address to open your flipbook library.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        {otpRequired ? 'Sending OTP...' : 'Opening library...'}
                      </>
                    ) : otpRequired ? (
                      <>
                        Send OTP
                        <ChevronRight size={15} />
                      </>
                    ) : (
                      <>
                        <BookOpen size={15} />
                        Access Library
                        <ChevronRight size={15} />
                      </>
                    )}
                  </button>
                </form>
              </div>


              {savedSession ? (
                <button
                  type="button"
                  onClick={openSavedLibrary}
                  className="w-full text-left bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:bg-primary/[0.03] transition-all animate-fade-in-up group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck size={18} className="text-emerald-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-700 text-foreground">
                          Continue to your library
                        </p>
                        <span className="inline-flex items-center gap-1 text-[10px] font-600 uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
                          <CheckCircle size={10} />
                          Verified
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5 min-w-0">
                        <Mail size={12} className="flex-shrink-0" />
                        <span className="truncate font-500 text-foreground">
                          {savedSession.email}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <BookOpen size={12} />
                          {savedSession.books.length === 1
                            ? '1 book'
                            : `${savedSession.books.length} books`}
                        </span>
                        <span>Tap to open your flipbooks</span>
                      </div>
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-muted-foreground group-hover:text-primary flex-shrink-0 mt-1 transition-colors"
                    />
                  </div>
                </button>
              ) : null}
            </>
          )}

          {step === 'otp' && (
            <div className="bg-card border border-border rounded-xl p-6 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Lock size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-base font-700 text-foreground">Enter Your OTP</h1>
                  <p className="text-xs text-muted-foreground">
                    Step 2 of 2 — Check your inbox
                  </p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  OTP sent to <span className="font-600 text-foreground">{email}</span>
                </p>
              </div>

              <div
                className="flex items-center justify-center gap-2 mb-4"
                onPaste={handleOTPPaste}
              >
                {otp.map((digit, i) => (
                  <input
                    key={`otp-box-${i}`}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(i, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(i, e)}
                    className={`otp-input-box ${digit ? 'filled' : ''} ${
                      otpError ? 'border-red-400' : ''
                    }`}
                    aria-label={`OTP digit ${i + 1}`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="text-red-500 text-xs text-center mb-3">{otpError}</p>
              )}

              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.join('').length < 6}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed mb-3"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle size={15} />
                    Verify & View Flipbooks
                  </>
                )}
              </button>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setStep('email');
                    setOtp(['', '', '', '', '', '']);
                    setOtpError('');
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft size={12} />
                  Change email
                </button>
                <button
                  onClick={handleResend}
                  disabled={resendCountdown > 0 || loading}
                  className="flex items-center gap-1 text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline transition-colors font-500"
                >
                  <RefreshCw size={12} />
                  {resendCountdown > 0
                    ? `Resend in ${resendCountdown}s`
                    : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
